import { relations } from 'drizzle-orm';
import { index, integer, numeric, pgEnum, pgTable, timestamp, uuid } from 'drizzle-orm/pg-core';
import * as z from 'zod';
import { patientTherapySequence, patientTherapySequenceMedicine } from './patient-therapy.js';
import { batches } from './stocks.js';
import { timestamps } from './utils/index.js';
import { userActions } from './utils/user-actions.js';
import { uuidv7 } from './utils/uuidv7.js';
/** The cleanroom step a scale reading was recorded in (see the cleanroom state machine). */
export const ProductionWeighingStep = z.enum([
    'EMPTY_SYRINGE',
    'SOLVENT',
    'DRUG',
    'CARRIER_PRIME_SYRINGE',
    'CARRIER_BASELINE',
    'EMPTY_CONTAINER',
    'PRIMED_CONTAINER',
    'WITHDRAWAL',
    'FINAL_CONTAINER',
    'TOPUP',
    'DRAWN_DRUG',
]);
/** A production_details row is a scale reading, or the PREPARATION_START marker saved once the cleanroom material check is done. */
export const ProductionDetailStep = z.enum([...ProductionWeighingStep.options, 'PREPARATION_START']);
export const productionWeighingStepEnum = pgEnum('production_weighing_step', [
    ProductionWeighingStep.enum.EMPTY_SYRINGE,
    ProductionWeighingStep.enum.SOLVENT,
    ProductionWeighingStep.enum.DRUG,
    ProductionWeighingStep.enum.CARRIER_PRIME_SYRINGE,
    ProductionWeighingStep.enum.CARRIER_BASELINE,
    ProductionWeighingStep.enum.EMPTY_CONTAINER,
    ProductionWeighingStep.enum.PRIMED_CONTAINER,
    ProductionWeighingStep.enum.WITHDRAWAL,
    ProductionWeighingStep.enum.FINAL_CONTAINER,
    ProductionWeighingStep.enum.TOPUP,
    ProductionWeighingStep.enum.DRAWN_DRUG,
    ProductionDetailStep.enum.PREPARATION_START,
]);
/**
 * Every scale reading taken while a prescription was compounded in the cleanroom, plus one
 * PREPARATION_START row (no weight, substanceIndex 0) for when the material check finished.
 * The label derives the actual quantity from the DRUG / DRAWN_DRUG rows:
 * allocatedDose × actualWeightGrams / expectedWeightGrams.
 */
export const productionDetails = pgTable('production_details', {
    id: uuidv7('id').primaryKey(),
    therapySequenceId: uuid('therapy_sequence_id')
        .notNull()
        .references(() => patientTherapySequence.id, { onDelete: 'cascade' }),
    // null for preparation-level steps (carrier, final container)
    medicineId: uuid('medicine_id').references(() => patientTherapySequenceMedicine.id, { onDelete: 'set null' }),
    // the vial batch the drug was drawn from (DRUG / DRAWN_DRUG rows); null for older rows
    batchId: uuid('batch_id').references(() => batches.id, { onDelete: 'set null' }),
    // the scale's alibi memory number for this reading; null without a scale or for older rows
    scaleMemoryNumber: integer('scale_memory_number'),
    step: productionWeighingStepEnum('step').notNull(),
    // index into the compounding engine's substances (one per vial allocation, not per medicine)
    substanceIndex: integer('substance_index').notNull(),
    // part of the medicine's prescribed dose this substance covers, in the dose unit
    allocatedDose: numeric('allocated_dose', { mode: 'number' }),
    expectedWeightGrams: numeric('expected_weight_grams', { mode: 'number' }),
    // null for the PREPARATION_START marker
    actualWeightGrams: numeric('actual_weight_grams', { mode: 'number' }),
    recordedAt: timestamp('recorded_at').notNull(),
    ...timestamps(),
    ...userActions(),
}, t => [
    // the label and report fetch a prescription's readings in step order
    index('idx_production_details_sequence_recorded').on(t.therapySequenceId, t.recordedAt),
    index('idx_production_details_medicine').on(t.medicineId),
]);
export const productionDetailsRelations = relations(productionDetails, ({ one }) => ({
    therapySequence: one(patientTherapySequence, {
        fields: [productionDetails.therapySequenceId],
        references: [patientTherapySequence.id],
    }),
    medicine: one(patientTherapySequenceMedicine, {
        fields: [productionDetails.medicineId],
        references: [patientTherapySequenceMedicine.id],
    }),
    batch: one(batches, {
        fields: [productionDetails.batchId],
        references: [batches.id],
    }),
}));
//# sourceMappingURL=production-details.js.map